/**
 * NIHARA BLOG — article.js
 * Premium editorial article page interactions
 * Vanilla ES6+, zero dependencies
 */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* =========================================================================
     1. Reading Progress Bar
     ========================================================================= */
  function initReadingProgress() {
    const bar = document.getElementById('nb-progress-bar');
    const article = document.querySelector('.nb-article-body');
    if (!bar || !article) return;

    let ticking = false;

    function update() {
      const articleTop    = article.getBoundingClientRect().top + window.scrollY;
      const articleHeight = article.offsetHeight;
      const windowHeight  = window.innerHeight;
      const scrolled      = window.scrollY - articleTop + windowHeight * 0.3;
      const progress      = Math.max(0, Math.min(100, (scrolled / articleHeight) * 100));

      bar.style.width = progress + '%';
      bar.parentElement.setAttribute('aria-valuenow', Math.round(progress));
      ticking = false;
    }

    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  }

  /* =========================================================================
     2. Scroll Reveal
     ========================================================================= */
  function initReveal() {
    if (prefersReducedMotion) {
      document.querySelectorAll('.nb-reveal').forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.nb-reveal').forEach(el => observer.observe(el));
  }

  /* =========================================================================
     3. Auto Table of Contents
     ========================================================================= */
  function initTableOfContents() {
    const articleBody = document.querySelector('.nb-article-body');
    const tocTarget   = document.querySelector('[data-toc-target]');
    if (!articleBody || !tocTarget) return;

    const headings = Array.from(articleBody.querySelectorAll('h2, h3'));
    if (headings.length < 3) {
      tocTarget.closest('.nb-toc')?.style.setProperty('display', 'none');
      return;
    }

    function slugify(text) {
      return text.toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    const list = document.createElement('ol');
    list.className = 'nb-toc__list';

    headings.forEach((heading, i) => {
      if (!heading.id) {
        heading.id = slugify(heading.textContent) || `section-${i}`;
      }

      const li = document.createElement('li');
      li.className = `nb-toc__item nb-toc__item--${heading.tagName.toLowerCase()}`;

      const link = document.createElement('a');
      link.className = 'nb-toc__link';
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.replace(/#/g, '').trim();

      link.addEventListener('click', (e) => {
        e.preventDefault();
        heading.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
        // Offset for sticky header
        setTimeout(() => window.scrollBy(0, -80), 10);
      });

      li.appendChild(link);
      list.appendChild(li);
    });

    tocTarget.appendChild(list);

    // Active heading highlight
    const linkEls = list.querySelectorAll('.nb-toc__link');

    const headingObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const id = entry.target.id;
        const tocLink = list.querySelector(`[href="#${id}"]`);
        if (tocLink) tocLink.classList.toggle('is-active', entry.isIntersecting);
      });
    }, { rootMargin: '-80px 0px -70% 0px', threshold: 0 });

    headings.forEach(h => headingObserver.observe(h));
  }

  /* =========================================================================
     4. Heading Anchor Links
     ========================================================================= */
  function initHeadingAnchors() {
    const articleBody = document.querySelector('.nb-article-body');
    if (!articleBody) return;

    articleBody.querySelectorAll('h2, h3').forEach(heading => {
      if (!heading.id) return;

      const anchor = document.createElement('a');
      anchor.className = 'nb-heading-anchor';
      anchor.href = `#${heading.id}`;
      anchor.setAttribute('aria-label', `Copy link to: ${heading.textContent}`);
      anchor.innerHTML = '#';

      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        const url = window.location.href.split('#')[0] + `#${heading.id}`;
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(() => showToast('Section link copied!'));
        } else {
          window.history.replaceState(null, '', `#${heading.id}`);
          showToast('Link updated!');
        }
      });

      heading.appendChild(anchor);
    });
  }

  /* =========================================================================
     5. Social Share Buttons
     ========================================================================= */
  function initShare() {
    const shareButtons = document.querySelectorAll('.nb-share__btn');
    if (!shareButtons.length) return;

    const url   = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(document.title);

    const shareUrls = {
      twitter:   `https://twitter.com/intent/tweet?text=${title}&url=${url}`,
      facebook:  `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      whatsapp:  `https://api.whatsapp.com/send?text=${title}%20${url}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${url}&description=${title}`,
    };

    shareButtons.forEach(btn => {
      const network = [...btn.classList].find(c => c.startsWith('nb-share__btn--') && c !== 'nb-share__btn')
        ?.replace('nb-share__btn--', '');

      if (network === 'copy') {
        btn.addEventListener('click', () => {
          if (navigator.clipboard) {
            navigator.clipboard.writeText(window.location.href).then(() => showToast('Link copied to clipboard!'));
          } else {
            const tmp = document.createElement('input');
            tmp.value = window.location.href;
            document.body.appendChild(tmp);
            tmp.select();
            document.execCommand('copy');
            document.body.removeChild(tmp);
            showToast('Link copied!');
          }
        });
        return;
      }

      if (shareUrls[network]) {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          window.open(shareUrls[network], 'share', 'width=600,height=480,noopener,noreferrer');
        });
      }
    });
  }

  /* =========================================================================
     6. Sticky Share Bar (desktop)
     ========================================================================= */
  function initStickyShare() {
    const stickyShare = document.querySelector('.nb-share--sticky');
    if (!stickyShare) return;

    const articleBody = document.querySelector('.nb-article-body');
    const footer = document.querySelector('footer, .nb-related');
    if (!articleBody) return;

    function update() {
      const bodyRect   = articleBody.getBoundingClientRect();
      const footerRect = footer?.getBoundingClientRect();
      const inView     = bodyRect.top < window.innerHeight * 0.5 && bodyRect.bottom > 0;
      const aboveFooter = !footerRect || footerRect.top > window.innerHeight;

      stickyShare.classList.toggle('is-visible', inView && aboveFooter);
    }

    window.addEventListener('scroll', update, { passive: true });
  }

  /* =========================================================================
     7. Image Lightbox
     ========================================================================= */
  function initLightbox() {
    const articleBody = document.querySelector('.nb-article-body');
    if (!articleBody) return;

    const images = Array.from(articleBody.querySelectorAll('img:not(.nb-no-lightbox)'));
    if (!images.length) return;

    // Create lightbox DOM
    const lb = document.createElement('div');
    lb.className = 'nb-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-label', 'Image viewer');
    lb.setAttribute('aria-modal', 'true');
    lb.innerHTML = `
      <img class="nb-lightbox__img" src="" alt="">
      <p class="nb-lightbox__caption"></p>
      <button class="nb-lightbox__close" aria-label="Close image viewer">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    `;
    document.body.appendChild(lb);

    const lbImg = lb.querySelector('.nb-lightbox__img');
    const lbCaption = lb.querySelector('.nb-lightbox__caption');
    let currentIndex = 0;

    function openAt(index) {
      currentIndex = index;
      const img = images[index];
      lbImg.src = img.src;
      lbImg.alt = img.alt;
      lbCaption.textContent = img.alt || img.closest('figure')?.querySelector('figcaption')?.textContent || '';
      lb.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      lb.querySelector('.nb-lightbox__close').focus();
    }

    function close() {
      lb.classList.remove('is-open');
      document.body.style.overflow = '';
      images[currentIndex]?.focus();
    }

    images.forEach((img, i) => {
      img.style.cursor = 'zoom-in';
      img.addEventListener('click', () => openAt(i));
    });

    lb.querySelector('.nb-lightbox__close').addEventListener('click', close);

    lb.addEventListener('click', (e) => {
      if (e.target === lb) close();
    });

    document.addEventListener('keydown', (e) => {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') openAt((currentIndex + 1) % images.length);
      if (e.key === 'ArrowLeft')  openAt((currentIndex - 1 + images.length) % images.length);
    });
  }

  /* =========================================================================
     8. Reading Time
     ========================================================================= */
  function initReadingTime() {
    const targets = document.querySelectorAll('[data-reading-time]');
    if (!targets.length) return;

    const body = document.querySelector('.nb-article-body');
    if (!body) return;

    const wordCount = body.innerText.trim().split(/\s+/).length;
    const minutes   = Math.max(1, Math.round(wordCount / 200));

    targets.forEach(el => {
      const textNode = el.querySelector('.nb-read-time-text');
      if (textNode) textNode.textContent = `${minutes} min read`;
    });
  }

  /* =========================================================================
     9. Back to Top
     ========================================================================= */
  function initBackToTop() {
    const btn = document.querySelector('.nb-back-to-top');
    if (!btn) return;

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          btn.classList.toggle('is-visible', window.scrollY > 600);
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });
  }

  /* =========================================================================
     10. Drop Cap fix
     ========================================================================= */
  function initDropCap() {
    const body = document.querySelector('.nb-article-body');
    if (!body) return;

    const firstEl = body.firstElementChild;
    // Skip if first element is not a paragraph
    if (!firstEl || firstEl.tagName !== 'P') {
      const firstPara = body.querySelector('p');
      if (firstPara) firstPara.classList.add('nb-dropcap');
    }
  }

  /* =========================================================================
     11. Toast helper
     ========================================================================= */
  function showToast(message) {
    let toast = document.querySelector('.nb-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'nb-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 2400);
  }

  /* =========================================================================
     12. Lazy Images
     ========================================================================= */
  function initLazyImages() {
    const lazyImgs = document.querySelectorAll('img[data-src]');
    if (!lazyImgs.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.getAttribute('data-src');
          const srcset = img.getAttribute('data-srcset');
          if (srcset) img.srcset = srcset;
          img.removeAttribute('data-src');
          img.removeAttribute('data-srcset');
          img.classList.add('is-loaded');
          observer.unobserve(img);
        }
      });
    }, { rootMargin: '300px' });

    lazyImgs.forEach(img => observer.observe(img));
  }

  /* =========================================================================
     Init
     ========================================================================= */
  function init() {
    initReadingProgress();
    initReveal();
    initTableOfContents();
    initHeadingAnchors();
    initShare();
    initStickyShare();
    initLightbox();
    initReadingTime();
    initBackToTop();
    initDropCap();
    initLazyImages();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  // Expose toast globally for other sections
  window.NiharaShowToast = showToast;

})();
